import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Category, GeoIntent, GeoUseCase, GeoOutputType, GeoDifficulty } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout/Layout";
import { TextFormattingToolbar } from "@/components/Home/TextFormattingToolbar";
import { CategoryCombobox } from "@/components/Home/CategoryCombobox";
import { 
  GeoIntentSelect, 
  GeoUseCaseSelect, 
  GeoModelSelect, 
  GeoTestedCheckbox, 
  GeoBanner, 
  GeoQualityProgress,
  GeoOutputTypeSelect,
  GeoDifficultySelect,
  GeoKeywordsInput,
  GeneratePromptButton,
  GeneratedPromptPreview,
} from "@/components/GEO";
import { createGeoMetadata } from "@/data/geo-constants";
import { useGeneratePrompt, type GeneratedPrompt } from "@/hooks/use-generate-prompt";

const CreatePrompt = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promptData, setPromptData] = useState({
    title: "",
    description: "",
    content: "",
    category: "" as Exclude<Category, 'all'>,
  });
  const [geoData, setGeoData] = useState({
    intent: "" as GeoIntent | "",
    useCase: "" as GeoUseCase | "",
    recommendedModel: "",
    testedByAuthor: false,
    outputType: "" as GeoOutputType | "",
    difficulty: "" as GeoDifficulty | "",
    keywords: [] as string[],
  });

  const { generate, isGenerating } = useGeneratePrompt();
  const [generatedPrompt, setGeneratedPrompt] = useState<GeneratedPrompt | null>(null);

  if (!user) {
    navigate("/auth", { replace: true });
    return null;
  }

  const handleGenerate = async () => {
    const result = await generate({
      intent: geoData.intent,
      useCase: geoData.useCase,
      model: geoData.recommendedModel || undefined,
      outputType: geoData.outputType || undefined,
      difficulty: geoData.difficulty || undefined,
      category: promptData.category || undefined,
    });
    if (result) {
      setGeneratedPrompt(result);
    }
  };

  const handleAcceptGenerated = () => {
    if (!generatedPrompt) return;
    setPromptData(prev => ({
      ...prev,
      title: generatedPrompt.title.slice(0, 70),
      description: generatedPrompt.description.slice(0, 300),
      content: generatedPrompt.content.slice(0, 5000),
    }));
    if (generatedPrompt.keywords?.length) {
      setGeoData(prev => ({ ...prev, keywords: generatedPrompt.keywords.slice(0, 10) }));
    }
    setGeneratedPrompt(null);
    toast.success("AI-generated content applied! Review and edit as needed.");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPromptData(prev => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (value: string) => {
    setPromptData(prev => ({ ...prev, content: value }));
  };

  const handleCategoryChange = (value: string) => {
    setPromptData(prev => ({ ...prev, category: value as Exclude<Category, 'all'> }));
  };

  // Validation: title, description (min 50 chars), content, intent, and useCase are required
  const isFormValid = 
    promptData.title.trim() && 
    promptData.description.trim().length >= 50 &&
    promptData.content.trim() && 
    geoData.intent && 
    geoData.useCase;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid) {
      const missingFields = [];
      if (!promptData.title.trim()) missingFields.push("title");
      if (!promptData.description.trim() || promptData.description.trim().length < 50) missingFields.push("description (min 50 characters)");
      if (!geoData.intent) missingFields.push("intent");
      if (!geoData.useCase) missingFields.push("use case");
      if (!promptData.content.trim()) missingFields.push("prompt content");
      
      toast.error(`Please fill in: ${missingFields.join(", ")}.`);
      return;
    }
    
    if (promptData.title.length > 200) {
      toast.error("Title cannot exceed 200 characters");
      return;
    }
    
    if (promptData.description && promptData.description.length > 500) {
      toast.error("Description cannot exceed 500 characters");
      return;
    }
    
    if (promptData.content.length < 10) {
      toast.error("Prompt content must be at least 10 characters");
      return;
    }
    
    if (promptData.content.length > 5000) {
      toast.error("Prompt content cannot exceed 5000 characters");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const geoMetadata = createGeoMetadata(
        geoData.intent as GeoIntent,
        geoData.useCase as GeoUseCase,
        geoData.recommendedModel || undefined,
        geoData.testedByAuthor,
        geoData.outputType as GeoOutputType || undefined,
        geoData.difficulty as GeoDifficulty || undefined,
        geoData.keywords
      );

      const insertData = {
        title: promptData.title,
        description: promptData.description || null,
        content: promptData.content,
        category: promptData.category || null,
        user_id: user.id,
        geo_metadata: geoMetadata,
      };
      
      const { data, error } = await supabase
        .from("prompts")
        .insert([insertData])
        .select();
      
      if (error) {
        console.error("Supabase insert error:", error);
        throw error;
      }
      
      toast.success("Prompt created successfully!");
      navigate("/prompts", { state: { newPromptId: data[0]?.id } });
    } catch (error: any) {
      console.error("Error creating prompt:", error);
      toast.error(error.message || "There was a problem creating your prompt. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <GeoBanner />
      <div className="container max-w-5xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold mb-8">Create New Prompt</h1>
        
        <div className="grid gap-8 lg:grid-cols-[1fr,280px]">
          {/* Main Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="title" className="text-sm font-medium">
                Title <span className="text-destructive">*</span>
              </label>
              <Input
                id="title"
                name="title"
                value={promptData.title}
                onChange={handleChange}
                placeholder="e.g., 'Professional Email Template for Client Follow-ups'"
                required
                maxLength={70}
                className="bg-background/50 border-border/30 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all placeholder:text-muted-foreground/60"
              />
              <p className="text-xs text-muted-foreground">
                {promptData.title.length}/70 · Include your use case for better AI discovery
              </p>
            </div>
          
          <div className="space-y-2">
            <label htmlFor="description" className="text-sm font-medium">
              Description <span className="text-destructive">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={promptData.description}
              onChange={handleChange}
              placeholder="Describe what this prompt does, when to use it, and expected results..."
              rows={3}
              minLength={50}
              maxLength={300}
              className="flex w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 border-border/30 focus:border-primary/50 transition-all resize-none"
            />
            <p className={`text-xs ${promptData.description.length >= 50 && promptData.description.length <= 160 ? 'text-green-600' : 'text-muted-foreground'}`}>
              {promptData.description.length}/300 · 50-160 characters is optimal for AI search snippets
            </p>
          </div>
          
          <div className="space-y-2">
            <label htmlFor="category" className="text-sm font-medium">
              Category <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <CategoryCombobox
              value={promptData.category}
              onChange={handleCategoryChange}
            />
          </div>

          <Separator className="my-6" />

          {/* GEO Metadata Section */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold">GEO Metadata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Intent <span className="text-destructive">*</span>
                  </label>
                  <GeoIntentSelect
                    value={geoData.intent}
                    onChange={(value) => setGeoData(prev => ({ ...prev, intent: value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Use Case <span className="text-destructive">*</span>
                  </label>
                  <GeoUseCaseSelect
                    value={geoData.useCase}
                    onChange={(value) => setGeoData(prev => ({ ...prev, useCase: value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Recommended Model <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <GeoModelSelect
                  value={geoData.recommendedModel}
                  onChange={(value) => setGeoData(prev => ({ ...prev, recommendedModel: value }))}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Output Type <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <GeoOutputTypeSelect
                    value={geoData.outputType}
                    onChange={(value) => setGeoData(prev => ({ ...prev, outputType: value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Difficulty <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <GeoDifficultySelect
                    value={geoData.difficulty}
                    onChange={(value) => setGeoData(prev => ({ ...prev, difficulty: value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Keywords <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <GeoKeywordsInput
                  value={geoData.keywords}
                  onChange={(keywords) => setGeoData(prev => ({ ...prev, keywords }))}
                />
              </div>

              <GeoTestedCheckbox
                checked={geoData.testedByAuthor}
                onChange={(checked) => setGeoData(prev => ({ ...prev, testedByAuthor: checked }))}
              />
            </CardContent>
          </Card>

          {/* AI Generate Section */}
          <div className="space-y-3">
            <GeneratePromptButton
              onClick={handleGenerate}
              isGenerating={isGenerating}
              disabled={!geoData.intent || !geoData.useCase}
            />
            {!geoData.intent || !geoData.useCase ? (
              <p className="text-xs text-muted-foreground text-center">
                Select Intent and Use Case above to enable AI generation
              </p>
            ) : null}

            {generatedPrompt && (
              <GeneratedPromptPreview
                generated={generatedPrompt}
                onAccept={handleAcceptGenerated}
                onRegenerate={handleGenerate}
                onDismiss={() => setGeneratedPrompt(null)}
                isRegenerating={isGenerating}
              />
            )}
          </div>

          <Separator className="my-6" />
          
          <div className="space-y-2">
            <label htmlFor="content" className="text-sm font-medium">
              Prompt Content <span className="text-destructive">*</span>
            </label>
            <TextFormattingToolbar
              value={promptData.content}
              onChange={handleContentChange}
              className="w-full bg-background/50 border-border/30 focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/50 transition-all"
            />
          </div>
          
            <div className="flex justify-end space-x-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting || !isFormValid}
                className="relative"
              >
                {isSubmitting ? "Creating..." : "Create Prompt"}
              </Button>
            </div>
          </form>

          {/* Quality Progress Sidebar */}
          <div className="hidden lg:block">
            <GeoQualityProgress 
              promptData={promptData}
              geoData={geoData}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreatePrompt;
